import axios from 'axios';
import {
  getRefreshToken,
  getAccessToken,
  setAccessToken,
  checkAndSaveRefreshTokenFromURL,
  checkAndSaveRedirectUrlFromURL,
  checkAndSaveCompanyFromURL,
  setUserInfo
} from '../utils/tokenManager';

// Backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// Create axios instance
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous token refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Refresh access token using refresh token
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('Refreshing access token...');
    const response = await axios.get(`${BACKEND_URL}/api/auth/token`, {
      headers: {
        'Authorization': refreshToken
      }
    });

    const newAccessToken = response.data.message?.accessToken;
    
    if (newAccessToken) {
      setAccessToken(newAccessToken);
      console.log('Access token refreshed successfully');
      return newAccessToken;
    } else {
      throw new Error('No access token in response');
    }
  } catch (error) {
    console.error('Failed to refresh access token:', error);
    throw error;
  }
};

/**
 * Sync user info from VIO API
 */
const syncUserInfo = async () => {
  try {
    console.log('Syncing user info from VIO...');
    const response = await api.get('/users/sync');
    
    if (response.data.status === 'Success' && response.data.message) {
      const userInfo = response.data.message;
      
      // Save to localStorage (always replace)
      setUserInfo(userInfo);
      
      console.log('User info synced successfully');
      return userInfo;
    } else {
      throw new Error('Invalid response from user sync');
    }
  } catch (error) {
    console.error('Failed to sync user info:', error);
    throw error;
  }
};

/**
 * Initialize tokens on app start
 * Check URL for refresh token, redirect URL, company, get access token, and sync user info
 * 
 * @returns {Object} { success: boolean, hasRefreshToken: boolean }
 */
export const initializeAuth = async () => {
  try {
    // Check if refresh token is in URL and save it
    const urlRefreshToken = checkAndSaveRefreshTokenFromURL();
    
    // Check if redirect URL is in URL and save it
    // This URL will be used for navigating to VIO voucher pages
    // Format: {redirectUrl}/viewRedeemedVoucher/{voucherNftId}
    checkAndSaveRedirectUrlFromURL();
    
    // Check if company is in URL and save it
    // Company info is provided by VIO miniapp URL instead of user/info API
    checkAndSaveCompanyFromURL();
    
    // Get refresh token (either from URL or localStorage)
    const refreshToken = urlRefreshToken || getRefreshToken();
    
    if (refreshToken) {
      // Get new access token
      await refreshAccessToken();
      
      // Sync user info from VIO
      await syncUserInfo();
      
      return { success: true, hasRefreshToken: true };
    } else {
      console.log('No refresh token found');
      return { success: false, hasRefreshToken: false };
    }
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    return { success: false, hasRefreshToken: !!getRefreshToken() };
  }
};

// Request interceptor - Add access token and refresh token to all requests
api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    
    if (accessToken) {
      config.headers.Authorization = accessToken;
    }
    
    // Add refresh token header for backend authentication
    if (refreshToken) {
      config.headers['x-refresh-token'] = refreshToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and refresh token
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        
        // Update the failed request with new token
        originalRequest.headers.Authorization = newAccessToken;
        
        // Process queued requests
        processQueue(null, newAccessToken);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // If refresh fails, redirect to login or show error
        console.error('Token refresh failed, user needs to re-authenticate');
        
        // Optional: Clear tokens and redirect
        // clearTokens();
        // window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get marketing page by ID (public endpoint - no auth required)
 */
export const getMarketingPageById = async (id) => {
  const response = await axios.get(`${BACKEND_URL}/api/marketing-pages/${id}`);
  return response.data;
};

/**
 * Get image URL from backend
 */
export const getImageUrl = (relativePath) => {
  if (!relativePath) return null;
  if (relativePath.startsWith('http')) return relativePath;
  return `${BACKEND_URL}${relativePath}`;
};

/**
 * Get campaign vouchers by campaign ID (public endpoint).
 * @param {string} tenantId - Required so backend can resolve the per-tenant API key.
 */
export const getCampaignVouchers = async (campaignId, page = 1, size = 50, tenantId) => {
  const response = await axios.get(`${BACKEND_URL}/api/campaign/${campaignId}/vouchers`, {
    params: { page, size, tenantId }
  });
  return response.data;
};

// Removed: getTokenList and getVouchersByIds
// Token logos are now included in the campaign voucher response (token.logo.imageUrl)
// Link vouchers use stored data from the marketing page config

export default api;
